import { FormattedMessage, useIntl } from 'react-intl';

import { CloseIcon } from '@/components/icons/close';
import { Button } from '@/components/ui/button';

type ActionButton = {
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
};

interface ActionToastProps {
  actionButtons: ActionButton[];
  /** Number of currently selected items shown in the toast. */
  selectedCount?: number;
  /** Called when the user clicks the close button to clear the selection. */
  onDismiss?: () => void;
}

export function ActionToast({ actionButtons, selectedCount, onDismiss }: ActionToastProps) {
  const intl = useIntl();
  const showLeadSection = onDismiss || (selectedCount !== undefined && selectedCount > 0);

  return (
    <div className="bg-fx-neutral-900/75 fixed bottom-10 left-1/2 flex -translate-x-1/2 items-center gap-1 rounded-lg px-2 py-1.5 shadow-lg">
      {showLeadSection && (
        <>
          <div className="flex items-center gap-1.5 px-2 py-1">
            {onDismiss && (
              <button
                type="button"
                onClick={onDismiss}
                className="flex h-5 w-5 shrink-0 cursor-pointer items-center justify-center rounded transition-colors hover:bg-white/15"
                aria-label={intl.formatMessage({
                  defaultMessage: 'Clear selection',
                  description: 'Action toast: accessible label for the close/clear button',
                })}
              >
                <CloseIcon className="text-fx-neutral-300" />
              </button>
            )}
            {selectedCount !== undefined && selectedCount > 0 && (
              <span className="text-sm font-medium text-fx-neutral-100 whitespace-nowrap">
                <FormattedMessage
                  defaultMessage="{count} selected"
                  description="Action toast: number of selected rows shown before the action buttons"
                  values={{ count: selectedCount }}
                />
              </span>
            )}
          </div>
          <div className="h-5 w-px shrink-0 bg-fx-neutral-500" />
        </>
      )}
      {actionButtons.map(button => {
        const Icon = button.icon;

        return (
          <Button
            key={button.label}
            variant="toast"
            size="sm"
            onClick={button.onClick}
          >
            {Icon}
            {button.label}
          </Button>
        );
      })}
    </div>
  );
}
